import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  Target, TrendingUp, TrendingDown, Plus, Calendar, CheckCircle2,
  AlertTriangle, Clock, Leaf, Activity, BarChart3, ArrowRight,
  RefreshCw, ChevronRight, AlertCircle, Sparkles, Save, X, Info,
  Sliders, Play, RotateCcw, Zap, Calculator
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Slider } from '../components/ui/slider';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ReferenceLine, ComposedChart
} from 'recharts';
import { apiFetch } from '../services/api_service';

const AMBITION_LEVELS = [
  { id: '1.5C', name: '1.5°C Aligned', description: '4.2% annual reduction', color: 'bg-green-500' },
  { id: 'well_below_2C', name: 'Well-Below 2°C', description: '2.5% annual reduction', color: 'bg-blue-500' },
  { id: '2C', name: '2°C Aligned', description: '1.23% annual reduction', color: 'bg-yellow-500' },
  { id: 'not_sbti', name: 'Custom Target', description: 'Not SBTi validated', color: 'bg-gray-500' }
];

const TARGET_TYPES = [
  { id: 'absolute', name: 'Absolute Reduction', description: 'Reduce total emissions by X%' },
  { id: 'intensity', name: 'Intensity Reduction', description: 'Reduce emissions per unit by X%' },
  { id: 'net_zero', name: 'Net Zero', description: 'Achieve net-zero emissions by year' }
];

const SCOPE_INCLUSIONS = [
  { id: 'scope_1_2', name: 'Scope 1 + 2' },
  { id: 'scope_1_2_3', name: 'Scope 1 + 2 + 3' },
  { id: 'scope_3_only', name: 'Scope 3 Only' }
];

// Progress Bar Component
const ProgressBar = ({ progress, required, onTrack }) => {
  const progressWidth = Math.min(Math.abs(progress), 100);
  const requiredWidth = Math.min(required, 100);
  
  return (
    <div className="relative h-6 bg-gray-100 rounded-full overflow-hidden">
      {/* Required position marker */}
      <div 
        className="absolute top-0 h-full w-1 bg-gray-400 z-10"
        style={{ left: `${requiredWidth}%` }}
      />
      {/* Actual progress */}
      <div 
        className={`h-full transition-all ${onTrack ? 'bg-green-500' : 'bg-red-500'}`}
        style={{ width: `${progressWidth}%` }}
      />
      {/* Labels */}
      <div className="absolute inset-0 flex items-center justify-between px-3 text-xs font-medium">
        <span className="text-white drop-shadow">{progress.toFixed(1)}% reduced</span>
        <span className="text-gray-600">{required.toFixed(1)}% required</span>
      </div>
    </div>
  );
};

// Target Card Component
const TargetCard = ({ target, onViewProgress }) => {
  const ambition = AMBITION_LEVELS.find(a => a.id === target.ambition);
  const isNetZero = target.target_type === 'net_zero';
  
  return (
    <Card className="hover:shadow-lg transition-shadow" data-testid={`target-${target.target_id}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${ambition?.color || 'bg-gray-500'} text-white`}>
              {isNetZero ? <Sparkles className="h-5 w-5" /> : <Target className="h-5 w-5" />}
            </div>
            <div>
              <h3 className="font-semibold">{target.name}</h3>
              <p className="text-sm text-gray-500">{ambition?.name}</p>
            </div>
          </div>
          <Badge variant={
            target.status === 'validated' ? 'default' :
            target.status === 'achieved' ? 'success' :
            target.status === 'off_track' ? 'destructive' : 'secondary'
          }>
            {target.status}
          </Badge>
        </div>

        <div className="grid grid-cols-3 gap-4 text-center border-t border-b py-4 my-4">
          <div>
            <p className="text-2xl font-bold">{target.base_year}</p>
            <p className="text-xs text-gray-500">Base Year</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-blue-600">
              {isNetZero ? 'Net Zero' : `${target.reduction_percentage}%`}
            </p>
            <p className="text-xs text-gray-500">
              {isNetZero ? 'Target' : 'Reduction'}
            </p>
          </div>
          <div>
            <p className="text-2xl font-bold">{target.target_year}</p>
            <p className="text-xs text-gray-500">Target Year</p>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Badge variant="outline">{target.scope_inclusion?.replace(/_/g, ' ')}</Badge>
            {target.has_interim_target && (
              <Badge variant="secondary">Interim: {target.interim_year}</Badge>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={() => onViewProgress(target)}>
            View Progress
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Target Creation Form
const CreateTargetForm = ({ onTargetCreated, onCancel }) => {
  const currentYear = new Date().getFullYear();
  const [formData, setFormData] = useState({
    name: '',
    target_type: 'absolute',
    ambition: '1.5C',
    scope_inclusion: 'scope_1_2',
    base_year: currentYear - 1,
    target_year: currentYear + 7,
    reduction_percentage: 42,
    has_interim_target: false,
    interim_year: currentYear + 3,
    interim_reduction_percentage: 21,
    scope3_categories_included: [],
    scope3_coverage_percentage: 0
  });
  const [creating, setCreating] = useState(false);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await apiFetch(`/sbti/targets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response && !response._error) {
        toast.success('Target created successfully');
        onTargetCreated(response);
      } else {
        toast.error(response?.message || 'Failed to create target');
      }
    } catch (error) {
      toast.error('Error creating target');
    } finally {
      setCreating(false);
    }
  };

  const selectedAmbition = AMBITION_LEVELS.find(a => a.id === formData.ambition);

  return (
    <Card className="border-2 border-green-200 bg-green-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Target className="h-5 w-5" />
          Create Climate Target
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic Info */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Target Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              placeholder="e.g., Near-Term SBTi Target 2030"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Target Type *</label>
            <Select 
              value={formData.target_type}
              onValueChange={(v) => setFormData({...formData, target_type: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TARGET_TYPES.map(type => (
                  <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Ambition Level */}
        <div>
          <label className="text-sm font-medium mb-3 block">Ambition Level *</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {AMBITION_LEVELS.map(level => (
              <div
                key={level.id}
                onClick={() => setFormData({...formData, ambition: level.id})}
                className={`p-3 rounded-lg border-2 cursor-pointer transition-all ${
                  formData.ambition === level.id 
                    ? 'border-green-500 bg-green-100' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className={`w-3 h-3 rounded-full ${level.color} mb-2`} />
                <p className="font-medium text-sm">{level.name}</p>
                <p className="text-xs text-gray-500">{level.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Years and Reduction */}
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium">Base Year *</label>
            <Input
              type="number"
              value={formData.base_year}
              onChange={(e) => setFormData({...formData, base_year: parseInt(e.target.value)})}
              min="2015"
              max={currentYear}
            />
          </div>
          <div>
            <label className="text-sm font-medium">Target Year *</label>
            <Input
              type="number"
              value={formData.target_year}
              onChange={(e) => setFormData({...formData, target_year: parseInt(e.target.value)})}
              min={currentYear}
              max="2100"
            />
          </div>
          <div>
            <label className="text-sm font-medium">
              {formData.target_type === 'net_zero' ? 'Residual Emissions %' : 'Reduction % *'}
            </label>
            <Input
              type="number"
              value={formData.reduction_percentage}
              onChange={(e) => setFormData({...formData, reduction_percentage: parseFloat(e.target.value)})}
              min="0"
              max="100"
            />
          </div>
        </div>

        {/* Scope */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium">Scope Inclusion *</label>
            <Select 
              value={formData.scope_inclusion}
              onValueChange={(v) => setFormData({...formData, scope_inclusion: v})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPE_INCLUSIONS.map(scope => (
                  <SelectItem key={scope.id} value={scope.id}>{scope.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {formData.scope_inclusion !== 'scope_1_2' && (
            <div>
              <label className="text-sm font-medium">Scope 3 Coverage %</label>
              <Input
                type="number"
                value={formData.scope3_coverage_percentage}
                onChange={(e) => setFormData({...formData, scope3_coverage_percentage: parseFloat(e.target.value)})}
                min="0"
                max="100"
                placeholder="Min 67% for SBTi"
              />
            </div>
          )}
        </div>

        {/* Interim Target */}
        <div className="p-4 bg-white rounded-lg">
          <label className="flex items-center gap-2 mb-4">
            <input
              type="checkbox"
              checked={formData.has_interim_target}
              onChange={(e) => setFormData({...formData, has_interim_target: e.target.checked})}
              className="rounded"
            />
            <span className="font-medium">Include Interim Target</span>
            <span className="text-xs text-gray-500">(Required for targets &gt;10 years)</span>
          </label>
          
          {formData.has_interim_target && (
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="text-sm font-medium">Interim Year</label>
                <Input
                  type="number"
                  value={formData.interim_year}
                  onChange={(e) => setFormData({...formData, interim_year: parseInt(e.target.value)})}
                  min={formData.base_year}
                  max={formData.target_year}
                />
              </div>
              <div>
                <label className="text-sm font-medium">Interim Reduction %</label>
                <Input
                  type="number"
                  value={formData.interim_reduction_percentage}
                  onChange={(e) => setFormData({...formData, interim_reduction_percentage: parseFloat(e.target.value)})}
                  min="0"
                  max={formData.reduction_percentage}
                />
              </div>
            </div>
          )}
        </div>

        {/* Summary */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <h4 className="font-medium mb-2">Target Summary</h4>
          <p className="text-sm text-gray-600">
            Reduce <strong>{formData.scope_inclusion?.replace(/_/g, ' ')}</strong> emissions by{' '}
            <strong>{formData.reduction_percentage}%</strong> from {formData.base_year} to {formData.target_year},{' '}
            aligned with <strong>{selectedAmbition?.name}</strong> pathway
            {formData.has_interim_target && (
              <> with interim target of {formData.interim_reduction_percentage}% by {formData.interim_year}</>
            )}.
          </p>
        </div>

        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onCancel}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!formData.name || creating}>
            {creating ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Create Target
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Progress View Component
const TargetProgressView = ({ target, onBack }) => {
  const [progress, setProgress] = useState(null);
  const [pathway, setPathway] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      try {
        const token = localStorage.getItem('token');
        const [progressRes, pathwayRes] = await Promise.all([
          apiFetch(`/sbti/targets/${target.target_id}/progress`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          apiFetch(`/sbti/targets/${target.target_id}/pathway`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);

        if (progressRes && !progressRes._error) {
          setProgress(progressRes);
        }
        if (pathwayRes && !pathwayRes._error) {
          setPathway(pathwayRes);
        }
      } catch (error) {
        console.error('Error fetching progress:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProgress();
  }, [target.target_id]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
        <p className="text-gray-500 mt-2">Loading progress data...</p>
      </div>
    );
  }

  const formatNumber = (n) => n?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button variant="ghost" onClick={onBack} className="mb-2">
            <ChevronRight className="h-4 w-4 mr-1 rotate-180" />
            Back to Targets
          </Button>
          <h2 className="text-2xl font-bold">{target.name}</h2>
          <p className="text-gray-500">
            {AMBITION_LEVELS.find(a => a.id === target.ambition)?.name} • {target.scope_inclusion?.replace(/_/g, ' ')}
          </p>
        </div>
        <Badge 
          variant={progress?.on_track ? 'default' : 'destructive'}
          className="text-lg px-4 py-2"
        >
          {progress?.on_track ? (
            <><CheckCircle2 className="h-5 w-5 mr-2" /> On Track</>
          ) : (
            <><AlertTriangle className="h-5 w-5 mr-2" /> Off Track</>
          )}
        </Badge>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Base Year Emissions</p>
            <p className="text-2xl font-bold">{formatNumber(progress?.base_year_emissions)}</p>
            <p className="text-xs text-gray-400">tCO₂e ({progress?.base_year})</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Current Emissions</p>
            <p className="text-2xl font-bold text-blue-600">{formatNumber(progress?.current_emissions)}</p>
            <p className="text-xs text-gray-400">tCO₂e ({progress?.current_year})</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Target Emissions</p>
            <p className="text-2xl font-bold text-green-600">{formatNumber(progress?.target_emissions)}</p>
            <p className="text-xs text-gray-400">tCO₂e ({progress?.target_year})</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-gray-500">Years Remaining</p>
            <p className="text-2xl font-bold">{progress?.years_remaining}</p>
            <p className="text-xs text-gray-400">to target year</p>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Reduction Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <ProgressBar 
            progress={progress?.percentage_reduction || 0}
            required={progress?.required_reduction || 0}
            onTrack={progress?.on_track || false}
          />
          <div className="flex justify-between mt-4 text-sm">
            <div>
              <span className="text-gray-500">Absolute Reduction:</span>{' '}
              <span className="font-semibold">{formatNumber(progress?.absolute_reduction)} tCO₂e</span>
            </div>
            <div>
              <span className="text-gray-500">Variance:</span>{' '}
              <span className={`font-semibold ${progress?.variance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {progress?.variance > 0 ? '+' : ''}{progress?.variance?.toFixed(1)}%
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Annual Reduction Rates */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Required Annual Reduction</p>
                <p className="text-3xl font-bold">{progress?.required_annual_reduction?.toFixed(2)}%</p>
              </div>
              <Activity className="h-10 w-10 text-gray-300" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Actual Annual Reduction</p>
                <p className={`text-3xl font-bold ${
                  progress?.actual_annual_reduction >= progress?.required_annual_reduction 
                    ? 'text-green-600' : 'text-red-600'
                }`}>
                  {progress?.actual_annual_reduction?.toFixed(2)}%
                </p>
              </div>
              {progress?.actual_annual_reduction >= progress?.required_annual_reduction ? (
                <TrendingUp className="h-10 w-10 text-green-300" />
              ) : (
                <TrendingDown className="h-10 w-10 text-red-300" />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pathway Chart (Simplified Table) */}
      {pathway?.pathway?.length > 0 && (
        <>
          {/* Main Decarbonization Pathway Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-green-600" />
                Decarbonization Pathway
              </CardTitle>
              <CardDescription>
                Target pathway vs actual emissions over time
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[400px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart
                    data={pathway.pathway.map(point => {
                      const actual = pathway.actuals?.find(a => a.year === point.year);
                      return {
                        year: point.year,
                        required: point.required_emissions,
                        actual: actual?.actual_emissions || null,
                        gap: actual ? actual.actual_emissions - point.required_emissions : null
                      };
                    })}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis 
                      dataKey="year" 
                      tick={{ fontSize: 12 }}
                      stroke="#6b7280"
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      stroke="#6b7280"
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                      label={{ 
                        value: 'Emissions (tCO₂e)', 
                        angle: -90, 
                        position: 'insideLeft',
                        style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12 }
                      }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                      }}
                      formatter={(value, name) => [
                        value ? `${formatNumber(value)} tCO₂e` : 'N/A',
                        name === 'required' ? 'Target Pathway' : 
                        name === 'actual' ? 'Actual Emissions' : name
                      ]}
                      labelFormatter={(label) => `Year ${label}`}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      formatter={(value) => (
                        <span className="text-sm">
                          {value === 'required' ? '🎯 Target Pathway' : 
                           value === 'actual' ? '📊 Actual Emissions' : value}
                        </span>
                      )}
                    />
                    {/* Target pathway area */}
                    <Area
                      type="monotone"
                      dataKey="required"
                      stroke="#10b981"
                      fill="#d1fae5"
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      name="required"
                    />
                    {/* Actual emissions line */}
                    <Line
                      type="monotone"
                      dataKey="actual"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      dot={{ fill: '#3b82f6', strokeWidth: 2, r: 5 }}
                      activeDot={{ r: 8, strokeWidth: 2 }}
                      name="actual"
                      connectNulls={false}
                    />
                    {/* Reference lines */}
                    <ReferenceLine 
                      y={pathway.target_emissions} 
                      stroke="#ef4444" 
                      strokeDasharray="3 3"
                      label={{ 
                        value: `Target: ${formatNumber(pathway.target_emissions)}`, 
                        position: 'right',
                        fill: '#ef4444',
                        fontSize: 11
                      }}
                    />
                    <ReferenceLine 
                      x={new Date().getFullYear()} 
                      stroke="#6b7280" 
                      strokeDasharray="3 3"
                      label={{ 
                        value: 'Now', 
                        position: 'top',
                        fill: '#6b7280',
                        fontSize: 11
                      }}
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              {/* Legend explanation */}
              <div className="flex items-center justify-center gap-6 mt-4 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-0.5 bg-green-500 border-dashed border-t-2 border-green-500" />
                  <span>Target Pathway (SBTi aligned)</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-1 bg-blue-500 rounded" />
                  <span>Actual Emissions</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Year-over-Year Change Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-blue-600" />
                Annual Emissions & Year-over-Year Change
              </CardTitle>
              <CardDescription>
                Track annual performance against reduction targets
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={(() => {
                      // Build data with YoY change
                      const actuals = pathway.actuals || [];
                      return actuals.map((point, idx) => {
                        const prevYear = idx > 0 ? actuals[idx - 1]?.actual_emissions : null;
                        const yoyChange = prevYear ? ((point.actual_emissions - prevYear) / prevYear * 100) : null;
                        const required = pathway.pathway.find(p => p.year === point.year)?.required_emissions;
                        return {
                          year: point.year,
                          actual: point.actual_emissions,
                          required: required,
                          yoyChange: yoyChange,
                          gap: required ? point.actual_emissions - required : null
                        };
                      });
                    })()}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="year" tick={{ fontSize: 12 }} stroke="#6b7280" />
                    <YAxis 
                      yAxisId="left"
                      tick={{ fontSize: 12 }} 
                      stroke="#6b7280"
                      tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                    />
                    <YAxis 
                      yAxisId="right"
                      orientation="right"
                      tick={{ fontSize: 12 }} 
                      stroke="#f59e0b"
                      tickFormatter={(value) => `${value?.toFixed(0)}%`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                      formatter={(value, name) => {
                        if (name === 'actual') return [`${formatNumber(value)} tCO₂e`, 'Actual Emissions'];
                        if (name === 'required') return [`${formatNumber(value)} tCO₂e`, 'Required'];
                        if (name === 'yoyChange') return [`${value?.toFixed(1)}%`, 'YoY Change'];
                        return [value, name];
                      }}
                    />
                    <Legend />
                    <Bar 
                      yAxisId="left"
                      dataKey="actual" 
                      fill="#3b82f6" 
                      name="Actual Emissions"
                      radius={[4, 4, 0, 0]}
                    />
                    <Bar 
                      yAxisId="left"
                      dataKey="required" 
                      fill="#d1fae5" 
                      stroke="#10b981"
                      strokeWidth={2}
                      name="Required"
                      radius={[4, 4, 0, 0]}
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="yoyChange"
                      stroke="#f59e0b"
                      strokeWidth={2}
                      dot={{ fill: '#f59e0b', r: 4 }}
                      name="YoY Change %"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Gap Analysis Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5 text-purple-600" />
                Gap Analysis: Actual vs Target
              </CardTitle>
              <CardDescription>
                Positive values indicate emissions above target (gaps to close)
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart
                    data={pathway.actuals?.map(point => {
                      const required = pathway.pathway.find(p => p.year === point.year)?.required_emissions;
                      return {
                        year: point.year,
                        gap: required ? point.actual_emissions - required : 0,
                        gapPercent: required ? ((point.actual_emissions - required) / required * 100) : 0
                      };
                    }) || []}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="year" tick={{ fontSize: 12 }} stroke="#6b7280" />
                    <YAxis 
                      tick={{ fontSize: 12 }} 
                      stroke="#6b7280"
                      tickFormatter={(value) => `${(value / 1000).toFixed(1)}k`}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px'
                      }}
                      formatter={(value, name) => {
                        if (name === 'gap') {
                          const formatted = formatNumber(Math.abs(value));
                          return [
                            value > 0 ? `+${formatted} tCO₂e (above target)` : `${formatted} tCO₂e (below target)`,
                            'Gap'
                          ];
                        }
                        return [value, name];
                      }}
                    />
                    <ReferenceLine y={0} stroke="#10b981" strokeWidth={2} />
                    <Area
                      type="monotone"
                      dataKey="gap"
                      stroke="#8b5cf6"
                      fill="#8b5cf6"
                      fillOpacity={0.3}
                      name="gap"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
              <div className="flex items-center justify-center gap-4 mt-4 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-purple-500 opacity-30 rounded" />
                  <span className="text-gray-600">Gap to target (above line = over target)</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Data Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="h-5 w-5 text-gray-600" />
                Detailed Pathway Data
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left py-3 px-4">Year</th>
                      <th className="text-right py-3 px-4">Target (tCO₂e)</th>
                      <th className="text-right py-3 px-4">Actual (tCO₂e)</th>
                      <th className="text-right py-3 px-4">Gap</th>
                      <th className="text-right py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pathway.pathway.slice(0, 12).map((point, idx) => {
                      const actual = pathway.actuals?.find(a => a.year === point.year);
                      const gap = actual ? actual.actual_emissions - point.required_emissions : null;
                      const onTrack = actual ? actual.actual_emissions <= point.required_emissions : null;
                      return (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium">{point.year}</td>
                          <td className="text-right py-3 px-4 text-green-600">{formatNumber(point.required_emissions)}</td>
                          <td className="text-right py-3 px-4 text-blue-600">
                            {actual ? formatNumber(actual.actual_emissions) : '-'}
                          </td>
                          <td className="text-right py-3 px-4">
                            {gap !== null && (
                              <span className={gap > 0 ? 'text-red-600' : 'text-green-600'}>
                                {gap > 0 ? '+' : ''}{formatNumber(gap)}
                              </span>
                            )}
                          </td>
                          <td className="text-right py-3 px-4">
                            {onTrack !== null && (
                              onTrack ? (
                                <Badge variant="outline" className="bg-green-50 text-green-600">
                                  <CheckCircle2 className="h-3 w-3 mr-1" />
                                  On Track
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="bg-red-50 text-red-600">
                                  <AlertTriangle className="h-3 w-3 mr-1" />
                                  Behind
                                </Badge>
                              )
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Recommendations */}
      {progress?.recommendations?.length > 0 && (
        <Card className={progress.on_track ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              {progress.on_track ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-red-600" />
              )}
              Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2">
              {progress.recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start gap-2">
                  <ChevronRight className="h-5 w-5 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Scenario Modeling */}
      <ScenarioModeling 
        target={target}
        progress={progress}
        pathway={pathway}
      />
    </div>
  );
};

// SBTi Requirements Component
const SBTiRequirements = () => {
  const [requirements, setRequirements] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRequirements = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await apiFetch(`/sbti/requirements`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response && !response._error) {
          setRequirements(response);
        }
      } catch (error) {
        console.error('Error fetching requirements:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRequirements();
  }, []);

  if (loading) {
    return (
      <div className="text-center py-8">
        <RefreshCw className="h-6 w-6 animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="bg-gradient-to-br from-green-50 to-blue-50 border-green-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Leaf className="h-5 w-5 text-green-600" />
            SBTi Target Requirements
          </CardTitle>
          <CardDescription>
            Science-Based Targets initiative validation requirements
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Near-Term Targets */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Near-Term Targets (5-10 years)
            </h4>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="p-4 bg-white rounded-lg">
                <p className="text-sm font-medium text-gray-500">Scope 1 & 2</p>
                <ul className="mt-2 space-y-1 text-sm">
                  <li>• <strong>1.5°C:</strong> {requirements?.near_term_targets?.scope_1_2?.["1.5C_aligned"]}</li>
                  <li>• <strong>Well-below 2°C:</strong> {requirements?.near_term_targets?.scope_1_2?.well_below_2C}</li>
                </ul>
              </div>
              <div className="p-4 bg-white rounded-lg">
                <p className="text-sm font-medium text-gray-500">Scope 3</p>
                <ul className="mt-2 space-y-1 text-sm">
                  <li>• <strong>Threshold:</strong> {requirements?.near_term_targets?.scope_3?.threshold}</li>
                  <li>• <strong>Coverage:</strong> {requirements?.near_term_targets?.scope_3?.minimum_coverage}</li>
                  <li>• <strong>Reduction:</strong> {requirements?.near_term_targets?.scope_3?.reduction}</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Long-Term Targets */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <Target className="h-4 w-4" />
              Long-Term / Net-Zero Targets
            </h4>
            <div className="p-4 bg-white rounded-lg">
              <ul className="space-y-1 text-sm">
                <li>• <strong>Timeframe:</strong> {requirements?.long_term_targets?.timeframe}</li>
                <li>• <strong>Reduction Required:</strong> {requirements?.long_term_targets?.reduction}</li>
                <li>• <strong>Residual Emissions:</strong> {requirements?.long_term_targets?.residual}</li>
              </ul>
            </div>
          </div>

          {/* Base Year */}
          <div>
            <h4 className="font-semibold mb-3 flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Base Year Requirements
            </h4>
            <div className="p-4 bg-white rounded-lg">
              <p className="text-sm mb-2"><strong>Recommendation:</strong> {requirements?.base_year?.recommendation}</p>
              <p className="text-sm font-medium text-gray-500 mb-1">Recalculation Triggers:</p>
              <ul className="space-y-1 text-sm text-gray-600">
                {requirements?.base_year?.recalculation_triggers?.map((trigger, idx) => (
                  <li key={idx}>• {trigger}</li>
                ))}
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Scenario Modeling Component
const ScenarioModeling = ({ target, progress, pathway }) => {
  const [reductionRate, setReductionRate] = useState(3.5);
  const [selectedScenario, setSelectedScenario] = useState(null);
  
  const currentYear = new Date().getFullYear();
  const currentEmissions = progress?.current_emissions || 0;
  const targetYear = target?.target_year || 2030;
  const targetEmissions = progress?.target_emissions || 0;
  const baseYearEmissions = progress?.base_year_emissions || 0;
  
  // Pre-built scenarios
  const SCENARIOS = [
    { 
      id: 'aggressive', 
      name: 'Aggressive', 
      rate: 5.0, 
      color: '#10b981',
      bgColor: 'bg-green-100',
      borderColor: 'border-green-500',
      icon: '🚀',
      description: 'Accelerated decarbonization with major investments'
    },
    { 
      id: 'moderate', 
      name: 'Moderate', 
      rate: 3.5, 
      color: '#3b82f6',
      bgColor: 'bg-blue-100',
      borderColor: 'border-blue-500',
      icon: '📊',
      description: 'Industry-standard reduction pace'
    },
    { 
      id: 'bau', 
      name: 'Business as Usual', 
      rate: 2.0, 
      color: '#f59e0b',
      bgColor: 'bg-yellow-100',
      borderColor: 'border-yellow-500',
      icon: '📉',
      description: 'Current trajectory without intervention'
    },
    { 
      id: 'target', 
      name: 'Target-Aligned', 
      rate: progress?.required_annual_reduction || 4.2, 
      color: '#8b5cf6',
      bgColor: 'bg-purple-100',
      borderColor: 'border-purple-500',
      icon: '🎯',
      description: 'Exactly what\'s needed to meet SBTi target'
    }
  ];

  // Calculate projection based on reduction rate
  const projectionData = useMemo(() => {
    const data = [];
    let emissions = currentEmissions;
    
    for (let year = currentYear; year <= targetYear + 2; year++) {
      // Target pathway point
      const targetPoint = pathway?.pathway?.find(p => p.year === year);
      const requiredEmissions = targetPoint?.required_emissions || 
        (baseYearEmissions * Math.pow(1 - (progress?.required_annual_reduction || 4.2) / 100, year - (progress?.base_year || 2019)));
      
      data.push({
        year,
        projected: Math.round(emissions),
        target: Math.round(requiredEmissions),
        gap: Math.round(emissions - requiredEmissions)
      });
      
      // Apply reduction for next year
      emissions = emissions * (1 - reductionRate / 100);
    }
    
    return data;
  }, [currentYear, currentEmissions, targetYear, reductionRate, pathway, baseYearEmissions, progress]);

  // Calculate key metrics
  const metrics = useMemo(() => {
    const finalProjected = projectionData.find(d => d.year === targetYear)?.projected || 0;
    const meetsTarget = finalProjected <= targetEmissions;
    const overshoot = finalProjected - targetEmissions;
    const overshootPercent = targetEmissions > 0 ? (overshoot / targetEmissions * 100) : 0;
    
    // Find year when target would be met
    let yearToTarget = null;
    for (const point of projectionData) {
      if (point.projected <= targetEmissions) {
        yearToTarget = point.year;
        break;
      }
    }
    
    // Calculate required rate to meet target
    const yearsRemaining = targetYear - currentYear;
    const requiredRate = yearsRemaining > 0 
      ? (1 - Math.pow(targetEmissions / currentEmissions, 1 / yearsRemaining)) * 100
      : 0;
    
    // Total reduction from current to projected end
    const totalReduction = currentEmissions > 0 
      ? ((currentEmissions - finalProjected) / currentEmissions * 100)
      : 0;
    
    return {
      finalProjected,
      meetsTarget,
      overshoot,
      overshootPercent,
      yearToTarget,
      requiredRate: Math.max(0, requiredRate),
      totalReduction,
      yearsRemaining
    };
  }, [projectionData, targetYear, targetEmissions, currentEmissions, currentYear]);

  const handleScenarioSelect = (scenario) => {
    setSelectedScenario(scenario.id);
    setReductionRate(scenario.rate);
  };

  const formatNumber = (n) => n?.toLocaleString(undefined, { maximumFractionDigits: 0 }) || '0';

  return (
    <Card className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 to-purple-50">
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2">
          <Sliders className="h-6 w-6 text-indigo-600" />
          Scenario Modeling
        </CardTitle>
        <CardDescription>
          Simulate different decarbonization pathways and see projected outcomes
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Pre-built Scenarios */}
        <div>
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <Zap className="h-4 w-4 text-yellow-500" />
            Quick Scenarios
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {SCENARIOS.map(scenario => (
              <div
                key={scenario.id}
                onClick={() => handleScenarioSelect(scenario)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md ${
                  selectedScenario === scenario.id 
                    ? `${scenario.bgColor} ${scenario.borderColor}` 
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="text-2xl mb-2">{scenario.icon}</div>
                <p className="font-semibold text-sm">{scenario.name}</p>
                <p className="text-lg font-bold" style={{ color: scenario.color }}>
                  {scenario.rate.toFixed(1)}%/yr
                </p>
                <p className="text-xs text-gray-500 mt-1">{scenario.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Custom Slider */}
        <div className="bg-white p-6 rounded-lg border">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-medium flex items-center gap-2">
              <Calculator className="h-4 w-4 text-gray-500" />
              Custom Annual Reduction Rate
            </h4>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={reductionRate}
                onChange={(e) => {
                  setReductionRate(parseFloat(e.target.value) || 0);
                  setSelectedScenario(null);
                }}
                className="w-20 text-center"
                min="0"
                max="15"
                step="0.1"
              />
              <span className="text-gray-500">% per year</span>
            </div>
          </div>
          <Slider
            value={[reductionRate]}
            onValueChange={(value) => {
              setReductionRate(value[0]);
              setSelectedScenario(null);
            }}
            min={0}
            max={10}
            step={0.1}
            className="mb-2"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>0% (No reduction)</span>
            <span>5% (Aggressive)</span>
            <span>10% (Very aggressive)</span>
          </div>
        </div>

        {/* Projection Chart */}
        <div>
          <h4 className="font-medium mb-3 flex items-center gap-2">
            <TrendingDown className="h-4 w-4 text-green-600" />
            Projected Emissions Pathway
          </h4>
          <div className="bg-white p-4 rounded-lg border">
            <div className="h-[350px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={projectionData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="year" tick={{ fontSize: 12 }} stroke="#6b7280" />
                  <YAxis 
                    tick={{ fontSize: 12 }} 
                    stroke="#6b7280"
                    tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                    label={{ 
                      value: 'Emissions (tCO₂e)', 
                      angle: -90, 
                      position: 'insideLeft',
                      style: { textAnchor: 'middle', fill: '#6b7280', fontSize: 12 }
                    }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'white', 
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                    }}
                    formatter={(value, name) => [
                      `${formatNumber(value)} tCO₂e`,
                      name === 'projected' ? `Projected (${reductionRate}%/yr)` : 
                      name === 'target' ? 'SBTi Target Pathway' : name
                    ]}
                  />
                  <Legend />
                  {/* Target pathway */}
                  <Area
                    type="monotone"
                    dataKey="target"
                    stroke="#10b981"
                    fill="#d1fae5"
                    strokeWidth={2}
                    strokeDasharray="5 5"
                    name="SBTi Target"
                    fillOpacity={0.3}
                  />
                  {/* Projected pathway */}
                  <Line
                    type="monotone"
                    dataKey="projected"
                    stroke="#8b5cf6"
                    strokeWidth={3}
                    dot={{ fill: '#8b5cf6', strokeWidth: 2, r: 4 }}
                    name="Projected"
                  />
                  {/* Target year marker */}
                  <ReferenceLine 
                    x={targetYear} 
                    stroke="#ef4444" 
                    strokeDasharray="3 3"
                    label={{ 
                      value: `Target Year`, 
                      position: 'top',
                      fill: '#ef4444',
                      fontSize: 11
                    }}
                  />
                  {/* Current year marker */}
                  <ReferenceLine 
                    x={currentYear} 
                    stroke="#6b7280" 
                    strokeDasharray="3 3"
                    label={{ 
                      value: 'Now', 
                      position: 'top',
                      fill: '#6b7280',
                      fontSize: 11
                    }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="grid md:grid-cols-2 gap-4">
          {/* Key Metrics */}
          <Card className={metrics.meetsTarget ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                {metrics.meetsTarget ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                )}
                Scenario Outcome
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Projected at {targetYear}:</span>
                <span className="font-bold text-lg">{formatNumber(metrics.finalProjected)} tCO₂e</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Target at {targetYear}:</span>
                <span className="font-bold text-lg text-green-600">{formatNumber(targetEmissions)} tCO₂e</span>
              </div>
              <div className="border-t pt-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Gap to Target:</span>
                  <span className={`font-bold text-lg ${metrics.overshoot > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {metrics.overshoot > 0 ? '+' : ''}{formatNumber(metrics.overshoot)} tCO₂e
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {metrics.overshoot > 0 
                    ? `${metrics.overshootPercent.toFixed(1)}% above target`
                    : `${Math.abs(metrics.overshootPercent).toFixed(1)}% below target`
                  }
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Recommendations */}
          <Card className="border-indigo-200">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-indigo-600" />
                Insights
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Reduction:</span>
                <span className="font-bold">{metrics.totalReduction.toFixed(1)}%</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Required Rate:</span>
                <span className="font-bold text-purple-600">{metrics.requiredRate.toFixed(2)}%/yr</span>
              </div>
              {metrics.yearToTarget && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Target Achieved:</span>
                  <Badge className="bg-green-100 text-green-700">{metrics.yearToTarget}</Badge>
                </div>
              )}
              <div className="border-t pt-3">
                {metrics.meetsTarget ? (
                  <p className="text-sm text-green-700">
                    ✅ At {reductionRate}% annual reduction, you will <strong>meet your SBTi target</strong> by {targetYear}
                    {metrics.yearToTarget && metrics.yearToTarget < targetYear && 
                      ` (actually by ${metrics.yearToTarget}!)`
                    }
                  </p>
                ) : (
                  <p className="text-sm text-red-700">
                    ⚠️ At {reductionRate}% annual reduction, you will <strong>miss your target</strong> by {formatNumber(metrics.overshoot)} tCO₂e. 
                    Increase to at least <strong>{metrics.requiredRate.toFixed(1)}%</strong> annual reduction.
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Action Items */}
        {!metrics.meetsTarget && (
          <Card className="border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-600" />
                Recommended Actions to Close the Gap
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Increase annual reduction rate from <strong>{reductionRate}%</strong> to <strong>{metrics.requiredRate.toFixed(1)}%</strong> ({(metrics.requiredRate - reductionRate).toFixed(1)}% increase needed)</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Reduce an additional <strong>{formatNumber(metrics.overshoot)}</strong> tCO₂e by {targetYear}</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Consider high-impact interventions: renewable energy transition, supply chain decarbonization, process efficiency improvements</span>
                </li>
                <li className="flex items-start gap-2">
                  <ChevronRight className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <span>Review Scope 3 emissions for reduction opportunities (typically 70%+ of footprint)</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Reset Button */}
        <div className="flex justify-end">
          <Button 
            variant="outline" 
            onClick={() => {
              setReductionRate(progress?.actual_annual_reduction || 2.5);
              setSelectedScenario(null);
            }}
            className="gap-2"
          >
            <RotateCcw className="h-4 w-4" />
            Reset to Current Rate
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Main Component
const SBTiTargetTracking = () => {
  const [activeTab, setActiveTab] = useState('targets');
  const [targets, setTargets] = useState([]);
  const [selectedTarget, setSelectedTarget] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchTargets = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const data = await apiFetch(`/sbti/targets`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setTargets(data.targets || []);
    } catch (error) {
      console.error('Error fetching targets:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTargets();
  }, [fetchTargets]);

  const handleViewProgress = (target) => {
    setSelectedTarget(target);
  };

  if (selectedTarget) {
    return (
      <div className="min-h-screen bg-gray-50 p-6" data-testid="sbti-tracking">
        <TargetProgressView 
          target={selectedTarget} 
          onBack={() => setSelectedTarget(null)} 
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="sbti-tracking">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Leaf className="h-8 w-8 text-green-600" />
          SBTi Target Tracking
        </h1>
        <p className="text-gray-500 mt-1">
          Science-Based Targets with progress tracking and pathway alignment
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="targets" className="gap-2">
            <Target className="h-4 w-4" />
            Targets
          </TabsTrigger>
          <TabsTrigger value="requirements" className="gap-2">
            <Leaf className="h-4 w-4" />
            SBTi Requirements
          </TabsTrigger>
        </TabsList>

        <TabsContent value="targets">
          <div className="space-y-6">
            {/* Actions */}
            <div className="flex justify-end">
              <Button onClick={() => setShowCreateForm(true)} disabled={showCreateForm}>
                <Plus className="h-4 w-4 mr-2" />
                Create Target
              </Button>
            </div>

            {/* Create Form */}
            {showCreateForm && (
              <CreateTargetForm 
                onTargetCreated={(data) => {
                  const target = data.target || data;
                  // Use functional update to avoid stale closure issue
                  setTargets(prevTargets => [...prevTargets, target]);
                  setShowCreateForm(false);
                }}
                onCancel={() => setShowCreateForm(false)}
              />
            )}

            {/* Targets List */}
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : targets.length > 0 ? (
              <div className="grid md:grid-cols-2 gap-4">
                {targets.map((target, idx) => (
                  <TargetCard 
                    key={idx} 
                    target={target} 
                    onViewProgress={handleViewProgress}
                  />
                ))}
              </div>
            ) : !showCreateForm ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Target className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No climate targets created yet</p>
                  <Button className="mt-4" onClick={() => setShowCreateForm(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Create Your First Target
                  </Button>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </TabsContent>

        <TabsContent value="requirements">
          <SBTiRequirements />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SBTiTargetTracking;
